/**
 * API endpoint paths for the ERP application
 * Updated for the real ERP API structure
 */

import { inventoryEndpoints } from '@/modules/inventory/api/endpoints'
import { supplyChainEndpoints } from '@/modules/supply-chain/api/endpoints'
import { selfServiceEndpoints } from '@/modules/self-services/api/endpoints'

export const endpoints = {
  // Authentication endpoints
  auth: {
    login: '/api/ApiAuthentication/Login',
    userDetails: (username: string) => `/api/ApiAuthentication/UserDetailsByUsername/${encodeURIComponent(username)}`,
  },

  // Dashboard endpoints
  dashboards: {
    finance: (branchId: number) => `/api/Dashbard/GetFinanceDashboard?branchId=${branchId}`,
    hr: (branchId: number) => `/api/Dashboard/GetHRDashboard?branchId=${branchId}`,
    receivables: (branchId: number) => `/api/Dashbard/GetReceivables/${branchId}`,
    payables: (branchId: number) => `/api/Dashbard/GetPayables/${branchId}`,
    incomeExpense: (branchId: number, year: number, isAccural: boolean) => 
      `/api/Dashbard/GetIncomeExpense/${branchId}?year=${year}&isAccural=${isAccural}`,
    cashFlow: (branchId: number, year: number) => 
      `/api/Dashbard/GetCashFlow/${branchId}?year=${year}`,
    profitLoss: (branchId: number, year: number) => 
      `/api/Dashbard/GetProfitLoss/${branchId}?year=${year}`,
  },

  // Employee management endpoints
  employees: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) => 
      `/api/Employee/GetAllEmployee/${branchId}/${pageNumber}/${pageSize}`,
    getReport: (branchId: number) => `/api/Employee/GetEmployeeReport/${branchId}`,
    getById: (branchId: number, id: number) => 
      `/api/Employee/GetemployeeById/${branchId}/${id}`,
    save: '/api/Employee/SaveEmployee',
    update: '/api/Employee/UpdateEmployee',
    delete: (id: number) => `/api/Employee/DeleteEmployee/${id}`,
  },

  // Employee Address endpoints
  empAddress: {
    /** Path: branchId / empId / pageNumber / pageSize (same pattern as Contract list). */
    getAll: (branchId: number, empId: number, pageNumber: number, pageSize: number) =>
      `/api/EmpAddress/GetAllEmployeeAddress/${branchId}/${empId}/${pageNumber}/${pageSize}`,
    getById: (id: number) => `/api/EmpAddress/GetEmployeeAddressById/${id}`,
    getByEmpId: (empId: number) => `/api/EmpAddress/GetEmployeeAddressByEmpId/${empId}`,
    create: '/api/EmpAddress/CreateEmployeeAddress',
    update: '/api/EmpAddress/UpdateEmployeeAddress',
    delete: (id: number) => `/api/EmpAddress/DeleteEmployeeAddress/${id}`,
  },

  // EmpDocument
  empDocument: {
    getList: (branchId: number, empId: number, pageNumber: number, pageSize: number) =>
      `/api/EmpDocument/GetDocuments/${branchId}/${empId}/${pageNumber}/${pageSize}`,
    getById: (id: number) => `/api/EmpDocument/GetDocumentById/${id}`,
    getByEmpId: (empId: number) => `/api/EmpDocument/GetDocumentsByEmpId/${empId}`,
    getDocumentTypes: '/api/EmpDocument/GetDocumentTypes',
    add: '/api/EmpDocument/AddDocument',
    update: (documentId: number) => `/api/EmpDocument/UpdateDocument/${documentId}`,
    delete: (documentId: number) => `/api/EmpDocument/DeleteDocument/${documentId}`,
  },

  entityDocument: {
    getByRef: (tableName: string, tableRowId: number) =>
      `/api/Document/GetByRef/${encodeURIComponent(tableName)}/${tableRowId}`,
    add: '/api/Document/AddDocument',
    delete: (documentId: number) => `/api/Document/DeleteDocument/${documentId}`,
  },

  // EmpQualification (GetQualificationTypes, GetAll: branchId query; GetById/Delete: id query)
  empQualification: {
    getQualificationTypes: '/api/EmpQualification/GetQualificationTypes',
    getAll: (branchId: number, empId: number, pageNumber: number, pageSize: number) =>
      `/api/EmpQualification/GetAll?branchId=${branchId}&empId=${empId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/EmpQualification/GetById?id=${id}`,
    getByEmpId: (empId: number) => `/api/EmpQualification/GetByEmpId/${empId}`,
    create: '/api/EmpQualification/Create',
    update: '/api/EmpQualification/Update',
    delete: (id: number) => `/api/EmpQualification/Delete?id=${id}`,
  },

  // EmpSkill (GetAll: branchId query; GetById/Delete: id query; GetByEmpId: empId query)
  empSkill: {
    getSkillTypes: '/api/EmpSkill/GetSkillTypes',
    // Backend expects pagination params (Swagger: pageNumber/pageSize)
    getAll: (branchId: number, empId: number, pageNumber: number, pageSize: number) =>
      `/api/EmpSkill/GetAll?branchId=${branchId}&empId=${empId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/EmpSkill/GetById?id=${id}`,
    getByEmpId: (empId: number) => `/api/EmpSkill/GetByEmpId?empId=${empId}`,
    save: '/api/EmpSkill/Save',
    update: '/api/EmpSkill/Update',
    delete: (id: number) => `/api/EmpSkill/Delete?id=${id}`,
  },

  // Training (GetAll: branchId query; GetById/Delete: id query; GetByEmpId: empId query)
  training: {
    // Swagger expects pagination params too (pageNumber/pageSize)
    getAll: (branchId: number, empId: number, pageNumber: number, pageSize: number) =>
      `/api/Training/GetAll?branchId=${branchId}&empId=${empId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/Training/GetById?id=${id}`,
    getByEmpId: (empId: number) => `/api/Training/GetByEmpId?empId=${empId}`,
    save: '/api/Training/Save',
    update: '/api/Training/Update',
    delete: (id: number) => `/api/Training/Delete?id=${id}`,
  },

  // Vehicle (GetVehicleTypes, GetAll: branchId query; GetById/Delete: id query; GetByEmpId: empId query)
  vehicle: {
    getVehicleTypes: '/api/Vehicle/GetVehicleTypes',
    // Swagger expects pagination params too
    getAll: (branchId: number, empId: number, pageNumber: number, pageSize: number) =>
      `/api/Vehicle/GetAll?branchId=${branchId}&empId=${empId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/Vehicle/GetById?id=${id}`,
    getByEmpId: (empId: number) => `/api/Vehicle/GetByEmpId?empId=${empId}`,
    save: '/api/Vehicle/Save',
    update: '/api/Vehicle/Update',
    delete: (id: number) => `/api/Vehicle/Delete?id=${id}`,
  },

  // EmpMedical (GetMedicalCompanies, GetMedicalClasses, GetAll with pagination; GetById/Delete: id query; GetByEmployeeId: employeeId query)
  empMedical: {
    getMedicalCompanies: '/api/EmpMedical/GetMedicalCompanies',
    getMedicalClasses: '/api/EmpMedical/GetMedicalClasses',
    getAll: (branchId: number, empId: number, pageNumber: number, pageSize: number) =>
      `/api/EmpMedical/GetAll?branchId=${branchId}&empId=${empId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/EmpMedical/GetById?id=${id}`,
    getByEmployeeId: (employeeId: number) => `/api/EmpMedical/GetByEmployeeId?employeeId=${employeeId}`,
    save: '/api/EmpMedical/Save',
    update: '/api/EmpMedical/Update',
    delete: (id: number) => `/api/EmpMedical/Delete?id=${id}`,
  },

  // EmpMediaLinks (GetAll: query params; GetById/Delete/GetByEmployeeId: path params)
  empMediaLinks: {
    getAll: (branchId: number, empId: number, pageNumber: number, pageSize: number) =>
      `/api/EmpMediaLinks/GetAll?branchId=${branchId}&empId=${empId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/EmpMediaLinks/GetById/${id}`,
    getByEmployeeId: (employeeId: number) => `/api/EmpMediaLinks/GetByEmployeeId/${employeeId}`,
    save: '/api/EmpMediaLinks/Save',
    update: '/api/EmpMediaLinks/Update',
    delete: (id: number) => `/api/EmpMediaLinks/Delete/${id}`,
  },

  // Employee lookup endpoints
  employeeLookups: {
    gender: '/api/Employee/GetGenderLookups',
    departments: '/api/Employee/GetDepartment',
    countries: '/api/Employee/GetCountryLookups',
    cities: '/api/Employee/GetCityLookups',
    professionIqama: '/api/Employee/GetProfessioninIqamaLookups',
    noticePeriod: '/api/Employee/GetNoticePeriodLookups',
    maritalStatus: '/api/Employee/GetMaritalStatusLookups',
    jobs: '/api/Employee/GetJobLookups',
    status: '/api/Employee/GetStatusLookups',
  },

  // Contract management endpoints
  contracts: {
    /** Swagger: GET …/GetAllcontract/{branchId}/{empId}/{pageNumber}/{pageSize} */
    getAll: (branchId: number, empId: number, pageNumber: number, pageSize: number) =>
      `/api/Contract/GetAllcontract/${branchId}/${empId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/Contract/GetcontractById/${id}/${branchId}`,
    getAllEmployees: (branchId: number) =>
      `/api/Contract/GetAllEmployee${branchId}`,
    getDepartments: (branchId: number) =>
      `/api/Contract/GetDepartment/${branchId}`,
    getLeaveBalances: (empId: number, branchId: number) =>
      `/api/Contract/GetLeaveBalances/${empId}/${branchId}`,
    getCurrency: '/api/Contract/GetCurrency',
    getSalaryType: '/api/Contract/GetSalaryType',
    getGrade: '/api/Contract/GetGrade',
    getSection: '/api/Contract/GetSection',
    save: '/api/Contract/SaveContract',
    update: '/api/Contract/UpdateContract',
    renew: '/api/Contract/RenewContract',
  },

  // Salary endpoints
  salary: {
    getByEmployeeId: (empId: number, contractId: number) =>
      `/api/Salary/GetSalariesByEmployeeId?empId=${empId}&contractId=${contractId}`,
  },

  // Attendance (Payroll)
  attendance: {
    addCombined: '/api/Attendance/AddCombinedAttendanceAsync',
    addSingle: '/api/Attendance/AddAttendanceAsync',
    getList: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/Attendance/GetAttendance/${branchId}/${pageNumber}/${pageSize}`,
    getByDepartmentEmployeeDateRange: (
      departmentId: number,
      empId: number,
      dateFrom: string,
      dateTo: string,
      pageNumber: number,
      pageSize: number,
      branchId: number
    ) => {
      const params = new URLSearchParams({
        departmentId: String(departmentId),
        empId: String(empId),
        dateFrom,
        dateTo,
        pageNumber: String(pageNumber),
        pageSize: String(pageSize),
        branchId: String(branchId),
      })
      return `/api/Attendance/GetAttendanceByDepartmentEmployeeDateRange?${params.toString()}`
    },
    getByMonthYear: (monthYear: string, branchId: number, pageNumber = 1, pageSize = 50) => {
      const mmYyyy = String(monthYear).replace(/\s/g, '-').trim()
      const q = `monthYear=${encodeURIComponent(mmYyyy)}&pageNumber=${pageNumber}&pageSize=${pageSize}&branchId=${branchId}`
      return `/api/Attendance/GetAttendanceByMonthYear?${q}`
    },
    update: '/api/Attendance/Update',
    getReport: (branchId: number, dateFrom: string, dateTo: string, departmentId: number) => {
      const params = new URLSearchParams({
        branchId: String(branchId),
        dateFrom,
        dateTo,
        departmentId: String(departmentId),
      })
      return `/api/Attendance/GetAttendanceReport?${params.toString()}`
    },
    post: (dateFrom: string, dateTo: string, departmentId: number, branchId: number) => {
      const params = new URLSearchParams({
        dateFrom,
        dateTo,
        departmentId: String(departmentId),
        branchId: String(branchId),
      })
      return `/api/Attendance/PostAttendanceAsync?${params.toString()}`
    },
  },

  // Incentive (Payroll)
  incentive: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/Incentive/GetAllIncentives/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number) => `/api/Incentive/${id}`,
    getDepartments: (branchId: number) =>
      `/api/Incentive/GetDepartment/${branchId}`,
    getEmployees: (branchId: number) =>
      `/api/Incentive/GetEmployee/${branchId}`,
    post: '/api/Incentive',
    put: '/api/Incentive',
    delete: (id: number) => `/api/Incentive/${id}`,
  },

  // Award Bonus (Payroll)
  awardBonus: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/AwardBonus/GetAll/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/AwardBonus/${id}/${branchId}`,
    save: '/api/AwardBonus/Save',
    update: '/api/AwardBonus/Update',
    delete: (id: number) => `/api/AwardBonus/${id}`,
  },

  // Payroll Payment (list, get by id, get payment amount, create, update, delete)
  payrollPayment: {
    /** Swagger: GET /api/PayrollPayment/GetAll/{branchId}/{departmentId}/{pageNumber}/{pageSize} */
    getAll: (branchId: number, departmentId: number, pageNumber: number, pageSize: number) =>
      `/api/PayrollPayment/GetAll/${branchId}/${departmentId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) => `/api/PayrollPayment/GetById/${id}/${branchId}`,
    getLinesByPaymentId: (id: number, branchId: number) => `/api/PayrollPayment/GetPayrollPaymentLinesByPaymentId/${id}/${branchId}`,
    /** Swagger: GET /api/PayrollPayment/GetPayrollPaymentLinesByPaymentIdWithPaging/{paymentId}/{branchId}/{pageNumber}/{pageSize} */
    getLinesByPaymentIdWithPaging: (paymentId: number, branchId: number, pageNumber: number, pageSize: number) =>
      `/api/PayrollPayment/GetPayrollPaymentLinesByPaymentIdWithPaging/${paymentId}/${branchId}/${pageNumber}/${pageSize}`,
    getByMonthYear: (monthYear: string, branchId: number) =>
      `/api/PayrollPayment/GetPayrollPaymentByMonthYear/${monthYear}/${branchId}`,
    getSummary: (paymentId: number, branchId: number) =>
      `/api/PayrollPayment/GetPayrollPaymentSummary/${paymentId}/${branchId}`,
    getPaymentAmount: (monthYear: string, departmentId: number, branchId: number) => {
      const params = new URLSearchParams({
        monthYear,
        departmentId: String(departmentId),
        branchId: String(branchId),
      })
      return `/api/PayrollPayment/GetPaymentAmount?${params.toString()}`
    },
    /** Swagger: POST /api/PayrollPayment/Save */
    save: '/api/PayrollPayment/Save',
    /** Swagger: GET /api/PayrollPayment/isAnyPaymentRemaining/{branchId}/{departmentId}/{monthYear} */
    isAnyPaymentRemaining: (branchId: number, departmentId: number, monthYear: string) =>
      `/api/PayrollPayment/isAnyPaymentRemaining/${branchId}/${departmentId}/${monthYear}`,
    create: '/api/PayrollPayment/CreatePayrollPayment',
    update: '/api/PayrollPayment/UpdatePayrollPayment',
    /** Swagger: PUT /api/PayrollPayment/Update/{id}/{branchId} */
    updateByIdBranch: (id: number, branchId: number) => `/api/PayrollPayment/Update/${id}/${branchId}`,
    delete: (id: number, branchId: number) => `/api/PayrollPayment/Delete/${id}/${branchId}`,
    /** Swagger: POST /api/PayrollPayment/PostData/{id}/{branchId} */
    postData: (id: number, branchId: number) => `/api/PayrollPayment/PostData/${id}/${branchId}`,
    refresh: (id: number, branchId: number) => `/api/PayrollPayment/Refresh/${id}/${branchId}`,
  },

  // Generate Payroll (Salary by month, Test, Post)
  payroll: {
    getSalaryByMonth: (branchId: number, monthYear: string, pageNumber: number, pageSize: number) => {
      const params = new URLSearchParams({ monthYear, pageNumber: String(pageNumber), pageSize: String(pageSize) })
      return `/api/Payroll/GetSalaryByMonth/${branchId}?${params.toString()}`
    },
    getSalaryById: (id: number) => `/api/Payroll/GetSalaryById/${id}`,
    getTest: (params: { monthYear: string; startDate: string; endDate: string; branchId: number; empId?: number; pageNumber: number; pageSize: number; save?: boolean }) => {
      const q = new URLSearchParams({
        monthYear: params.monthYear,
        startDate: params.startDate,
        endDate: params.endDate,
        branchId: String(params.branchId),
        empId: String(params.empId ?? 0),
        pageNumber: String(params.pageNumber),
        pageSize: String(params.pageSize),
      })
      if (params.save) q.set('save', 'true')
      return `/api/Payroll/GetTest?${q.toString()}`
    },
    getTestSalary: (branchId: number, monthYear: string) => {
      const params = new URLSearchParams({ monthYear })
      return `/api/Payroll/GetTestSalary/${branchId}?${params.toString()}`
    },
    updateTestSalary: '/api/Payroll/UpdateTestSalary',
    generatePayroll: '/api/Payroll/GeneratePayroll',
    getMissedEmployees: (params: { branchId: number; departmentId: number; monthYear: string; startDate: string; endDate: string }) => {
      const q = new URLSearchParams({
        branchId: String(params.branchId),
        departmentId: String(params.departmentId),
        monthYear: params.monthYear,
        startDate: params.startDate,
        endDate: params.endDate,
      })
      return `/api/Payroll/GetMissedEmployees?${q.toString()}`
    },
    getPayrollComparison: (params: { branchId: number; empId: number; monthYear1: string; monthYear2: string }) => {
      const q = new URLSearchParams({
        branchId: String(params.branchId),
        empId: String(params.empId),
        monthYear1: params.monthYear1,
        monthYear2: params.monthYear2,
      })
      return `/api/Payroll/GetPayrollComparison?${q.toString()}`
    },
    getReport: (params: { branchId: number; fromMonth: string; toMonth: string; pageNumber: number; pageSize: number }) => {
      const q = new URLSearchParams({
        branchId: String(params.branchId),
        fromMonth: params.fromMonth,
        toMonth: params.toMonth,
        pageNumber: String(params.pageNumber),
        pageSize: String(params.pageSize),
      })
      return `/api/Payroll/GetReport?${q.toString()}`
    },
  },

  // Shift endpoints (HR Settings)
  apiShift: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiShift/GetAll/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/ApiShift/GetById/${id}/${branchId}`,
    create: '/api/ApiShift/Create',
    update: '/api/ApiShift/Update',
    delete: (id: number) => `/api/ApiShift/Delete/${id}`,
  },

  // Bank management endpoints
  banks: {
    getAll: (branchId?: number) => {
      let url = '/api/Bank/GetAll'
      if (branchId) {
        url += `?branchId=${branchId}`
      }
      return url
    },
    getById: (id: number) => `/api/Bank/GetById?id=${id}`,
    getByEmpId: (empId: number) => `/api/Bank/GetByEmpId?empId=${empId}`,
    save: '/api/Bank/Save',
    update: '/api/Bank/Update',
    delete: (id: number) => `/api/Bank/Delete?id=${id}`,
  },

  // Company Banks (Settings) endpoints
  companyBanks: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/CompanyBanks/GetAll?branchId=${branchId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/CompanyBanks/GetById?id=${id}`,
    getLevel5Accounts: (branchId: number) => `/api/CompanyBanks/GetLevel5Accounts?branchId=${branchId}`,
    getCountryLookups: '/api/CompanyBanks/GetCountryLookups',
    getCityLookups: (countryId: number) => `/api/CompanyBanks/GetCityLookups?countryId=${countryId}`,
    getStateLookups: (cityId: number) => `/api/CompanyBanks/GetStateLookups?cityId=${cityId}`,
    save: '/api/CompanyBanks/Save',
    update: '/api/CompanyBanks/Update',
    delete: (id: number) => `/api/CompanyBanks/Delete?id=${id}`,
  },

  // Customer management endpoints (real ERP API)
  customers: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiCustomer/GetAllCustomers/${branchId}/${pageNumber}/${pageSize}`,
    getById: (branchId: number, id: number) =>
      `/api/ApiCustomer/GetCustomerById/${branchId}/${id}`,
    save: '/api/ApiCustomer/SaveCustomer',
    update: '/api/ApiCustomer/UpdateCustomer',
    delete: (id: number, branchId: number, ipAddress?: string) => {
      let url = `/api/ApiCustomer/DeleteCustomer/${id}?branchId=${branchId}`
      if (ipAddress) {
        url += `&ipAddress=${encodeURIComponent(ipAddress)}`
      }
      return url
    },
    getCountries: '/api/ApiCustomer/GetCountryLookups',
    getCities: (countryId?: number) => {
      const base = '/api/ApiCustomer/GetCityLookups'
      if (countryId !== undefined) {
        return `${base}?countryId=${countryId}`
      }
      return base
    },
    getStates: '/api/ApiBranch/GetStateLookups',
  },

  // Finance & sales shared endpoints
  finance: {
    customers: (branchId: number) => `/api/ApiInvoice/GetAllCustomers/${branchId}`,
    projects: (branchId: number, customerId: number) => `/api/ApiInvoice/GetProjects/${branchId}/${customerId}`,
    items: (branchId: number) => `/api/ApiInvoice/GetItemList?branchId=${branchId}`,
    taxRates: (branchId: number) => `/api/ApiInvoice/GetTaxRates/${branchId}`,
    paymentTerms: '/api/ApiInvoice/GetlookuppaymentTerms',
    currencies: '/api/ApiInvoice/GetAllcurrencies',
    invoiceCategories: '/api/ApiInvoice/GetlookupinvoiceCateogries',
    // ApiBills endpoints for General Entry
    getAllAccounts: (branchId: number) => `/api/ApiBills/GetAllAccounts/${branchId}`,
    getProject: (branchId: number) => `/api/ApiBills/GetProject/${branchId}`,
    getProfitCenter: (branchId?: number) => {
      const base = `/api/ApiBills/GetProfitCenter`
      if (branchId !== undefined) {
        return `${base}?branchId=${branchId}`
      }
      return base
    },
    getCostLocation: (branchId?: number) => {
      const base = `/api/ApiBills/GetCostLocation`
      if (branchId !== undefined) {
        return `${base}?branchId=${branchId}`
      }
      return base
    },
    getAllVendors: (branchId: number) => `/api/ApiBills/GetAllVendors/${branchId}`,
  },

  // Quotation management endpoints (real ERP API)
  quotations: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiQuotation/GetAllQuotation/${branchId}/${pageNumber}/${pageSize}`,
    byCustomer: (branchId: number, customerId?: number) => {
      const base = `/api/ApiQuotation/GetQuotationByCustomer/${branchId}`
      if (typeof customerId === 'number' && !Number.isNaN(customerId)) {
        return `${base}?CustomerId=${customerId}`
      }
      return base
    },
    getById: (branchId: number, id: number) =>
      `/api/ApiQuotation/GetQuotationById/${branchId}/${id}`,
    getLinesById: (branchId: number, id: number) =>
      `/api/ApiQuotation/GetQuotationLinesById/${branchId}?id=${id}`,
    insert: '/api/ApiQuotation/Insert',
    update: '/api/ApiQuotation/Update',
    convertToInvoice: (id: number, ipAddress?: string) => {
      let url = `/api/ApiQuotation/ConvertToInvoice?id=${id}`
      if (ipAddress) {
        url += `&ipAddress=${encodeURIComponent(ipAddress)}`
      }
      return url
    },
  },

  // Invoice management endpoints (real ERP API)
  invoices: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiInvoice/GetAllInvoiceList/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/ApiInvoice/GetInvoiceById/${id}/${branchId}`,
    getLines: (branchId: number) => `/api/ApiInvoice/GetAllInvoiceLines/${branchId}`,
    getLinesById: (id: number, branchId: number) =>
      `/api/ApiInvoice/GetAllInvoiceLinesbyId/${id}/${branchId}`,
    insert: '/api/ApiInvoice/Insert',
    update: '/api/ApiInvoice/Update',
    postData: (id: number) => `/api/ApiInvoice/PostData?id=${id}`,
  },

  // Credit Notes management endpoints (real ERP API)
  creditNotes: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiCreditNotes/GetAllCreditNotes/${branchId}/${pageNumber}/${pageSize}`,
    getById: (branchId: number, id: number) =>
      `/api/ApiCreditNotes/GetCreditNoteLineById/${branchId}/${id}`,
    getAllInvoices: (branchId: number, customerId?: number, creditNoteType?: number, excludeCreditNoteId?: number) => {
      let url = `/api/ApiCreditNotes/GetAllInvoice/${branchId}`
      const params = new URLSearchParams()
      if (customerId && customerId > 0) {
        params.set('customerId', String(customerId))
      }
      if (creditNoteType && creditNoteType > 0) {
        params.set('creditNoteType', String(creditNoteType))
      }
      if (excludeCreditNoteId && excludeCreditNoteId > 0) {
        params.set('excludeCreditNoteId', String(excludeCreditNoteId))
      }
      const qs = params.toString()
      return qs ? `${url}?${qs}` : url
    },
    getAllInvoiceLines: (branchId: number) =>
      `/api/ApiCreditNotes/GetAllInvoiceLines/${branchId}`,
    getInvLines: (id: number, branchId: number, excludeCreditNoteId?: number) => {
      let url = `/api/ApiCreditNotes/GetInvLines/${id}/${branchId}`
      if (excludeCreditNoteId && excludeCreditNoteId > 0) {
        url += `?excludeCreditNoteId=${excludeCreditNoteId}`
      }
      return url
    },
    insert: '/api/ApiCreditNotes/Insert',
    update: '/api/ApiCreditNotes/Update',
    postData: (id: number, ipAddress?: string) => {
      let url = `/api/ApiCreditNotes/PostData?id=${id}`
      if (ipAddress) {
        url += `&IpAddress=${encodeURIComponent(ipAddress)}`
      }
      return url
    },
  },

  // Projects management endpoints
  projects: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiProject/GetProjects/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/ApiProject/GetProject/${id}/${branchId}`,
    getCostLocation: '/api/ApiProject/GetCostLocation',
    getProfitCenter: '/api/ApiProject/GetProfitCenter',
    save: '/api/ApiProject/SaveProject',
    update: '/api/ApiProject/UpdateProject',
  },

  // Account management endpoints (Chart of Accounts)
  accounts: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiAccount/GetAllAccount/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/ApiAccount/GetAccountById/${id}/${branchId}`,
    getByParentId: (id: number, branchId: number) =>
      `/api/ApiAccount/GetAccountsByParentId/${id}/${branchId}`,
    getTransactionableAccounts: (branchId: number) =>
      `/api/ApiAccount/GetTransactionableAccounts/${branchId}`,
    generateCode: (parentId: number) =>
      `/api/ApiAccount/GenerateAccountCode/${parentId}`,
    save: '/api/ApiAccount/SaveAccount',
    update: '/api/ApiAccount/UpdateAccount',
    uploadExcel: (branchId: number) =>
      `/api/ApiAccount/UploadExcel/${branchId}`,
    downloadChart: (branchId: number) =>
      `/api/ApiAccount/DownloadChartOfAccounts/${branchId}`,
  },

  // Lookup/Reference data endpoints
  lookups: {
    departments: '/lookups/departments',     // GET - List of departments
    positions: '/lookups/positions',         // GET - List of positions
    countries: '/lookups/countries',         // GET - List of countries
    currencies: '/lookups/currencies',       // GET - List of currencies
    statuses: '/lookups/statuses',          // GET - Various status lists
  },

  // Inbox / Workflow - Task Inbox list & detail
  inboxTasks: {
    taskNames: '/api/InboxTasks/TaskNames',
    approvalStatuses: '/api/InboxTasks/ApprovalStatuses',
    getAll: (taskId: number, statusId: number, branchId: number, pageNumber: number, pageSize: number) =>
      `/api/InboxTasks/GetAll?taskId=${taskId}&statusId=${statusId}&branchId=${branchId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/InboxTasks/GetById/${id}`,
    getNotifications: (branchId: number) => `/api/InboxTasks/GetNotifications?branchId=${branchId}`,
  },

  // Permissions (Security — modules & menus for role permissions grid)
  permissions: {
    getModules: '/api/Permissions/GetModules',
    /** Logged-in user's permission for a module/menu in a branch (omit menuName when empty = module-level only). */
    getPermissionForLoggedInUser: (
      moduleName: string,
      menuName: string,
      branchId: number,
      roleId: number
    ) => {
      const q = new URLSearchParams()
      q.set('moduleName', moduleName)
      q.set('branchId', String(branchId))
      q.set('roleId', String(roleId))
      const menu = menuName.trim()
      if (menu) q.set('menuName', menu)
      return `/api/Permissions/GetPermissionForLoggedInUser?${q.toString()}`
    },
    getMenu: (moduleName: string, roleId?: number) => {
      const base = `/api/Permissions/GetMenu?moduleName=${encodeURIComponent(moduleName)}`
      if (roleId != null && roleId > 0) return `${base}&roleId=${roleId}`
      return base
    },
    getPermissions: (roleId: number, moduleId: number, branchId: number) =>
      `/api/Permissions/GetPermissions?roleId=${roleId}&moduleId=${moduleId}&branchId=${branchId}`,
    save: '/api/Permissions/FormSave',
  },

  // Roles (Security)
  roles: {
    getAll: (branchId: number, pageNo: number, pageSize: number) =>
      `/api/Roles/GetAll?pageNo=${pageNo}&pageSize=${pageSize}&branchId=${branchId}`,
    getById: (id: number) => `/api/Roles/GetById?id=${id}`,
    save: '/api/Roles/Save',
    update: '/api/Roles/Update',
  },

  // User (for Requested By -> empId -> employee name)
  user: {
    getById: (branchId: number, id: number) => `/api/User/GetUserById/${branchId}/${id}`,
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/User/GetAllUsers/${branchId}/${pageNumber}/${pageSize}`,
    getAllRoles: (branchId: number) => `/api/User/GetAllRoles/${branchId}`,
    getAllEmployees: (branchId: number) => `/api/User/GetAllEmployees/${branchId}`,
    create: '/api/User/CreateUser',
    update: '/api/User/UpdateUser',
  },

  // Approval route detail (approvers list, submit approval)
  approvalRouteDetail: {
    getApprovalUsers: (branchId: number, taskId: number) =>
      `/api/ApprovalRouteDetail/GetApprovalUsers?branchId=${branchId}&taskId=${taskId}`,
    approveRequest: (id: number, approvalNote: string, approvalStatusId: number) =>
      `/api/ApprovalRouteDetail/ApproveRequest?id=${id}&approvalNote=${encodeURIComponent(approvalNote)}&approvalStatusId=${approvalStatusId}`,
  },

  // Approval Template (Workflow Approval Master)
  approvalTemplate: {
    getUsers: (branchId: number, departmentId: number) =>
      `/api/ApprovalTemplate/GetUsers?branchId=${branchId}&departmentId=${departmentId}`,
    getByTaskId: (taskId: number, branchId: number) =>
      `/api/ApprovalTemplate/GetByTaskId?taskId=${taskId}&branchId=${branchId}`,
    save: '/api/ApprovalTemplate/Save',
  },

  // (moved to `modules/self-services/api/endpoints.ts`)

  // Lookup Management endpoints
  lookupManagement: {
    getAllCodeTypes: '/api/ApiLookup/GetAllCodeTypes',
    getCodeTypeById: (id: number) => `/api/ApiLookup/GetCodeTypeById/${id}`,
    getAllLookups: '/api/ApiLookup/GetAllLookups',
    getLookupsById: (lookupCodeId: number, parentId?: number) => {
      const url = `/api/ApiLookup/GetLookupsById/${lookupCodeId}`
      if (parentId !== undefined) {
        return `${url}?parentId=${parentId}`
      }
      return url
    },
    formSave: '/api/ApiLookup/FormSave',
  },

  // Tax Rate (VAT settings)
  taxRate: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/TaxRate/GetAllTaxRates/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/TaxRate/GetTaxRateById/${id}/${branchId}`,
    save: '/api/TaxRate/SaveTaxRate',
    update: '/api/TaxRate/UpdateTaxRate',
    delete: (id: number) => `/api/TaxRate/DeleteTaxRate/${id}`,
  },

  // Branch management endpoints
  branches: {
    getAll: '/api/ApiBranch/GetAllBranches',
    getById: (id: number) => `/api/ApiBranch/GetBranchById/${id}`,
    save: '/api/ApiBranch/SaveBranch',
    update: '/api/ApiBranch/UpdateBranch',
    delete: (id: number) => `/api/ApiBranch/DeleteBranch/${id}`,
    getCountries: '/api/ApiBranch/GetCountryLookups',
    getCities: (countryId?: number) => {
      const base = '/api/ApiBranch/GetCityLookups'
      if (countryId !== undefined) {
        return `${base}?countryId=${countryId}`
      }
      return base
    },
    getCurrencies: '/api/ApiBranch/GetCurrencyLookups',
  },

  // Feature-based endpoints (module-owned)
  ...inventoryEndpoints,
  ...supplyChainEndpoints,
  ...selfServiceEndpoints,

  // Report endpoints
  reports: {
    finance: '/reports/finance',            // GET - Financial reports
    hr: '/reports/hr',                      // GET - HR reports
    custom: '/reports/custom',              // POST - Generate custom report
    customerLedger: (fromDate: string, toDate: string, branchId: number, pageNumber: number, pageSize: number, customerId?: number) => {
      const base = `/api/LedgerReport/GetCustomerLedgerReport/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      // Always include customerId - send 0 if not provided (for "All Customers")
      params.append('customerId', String(customerId !== undefined ? customerId : 0))
      return `${base}?${params.toString()}`
    },
    accountLedger: (fromDate: string, toDate: string, branchId: number, pageNumber: number, pageSize: number, accountId?: number) => {
      const base = `/api/LedgerReport/GetAccountLedgerReport/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      // Always include accountId - send 0 if not provided (for "All Accounts")
      params.append('accountId', String(accountId !== undefined ? accountId : 0))
      return `${base}?${params.toString()}`
    },
    salesRevenue: (fromDate: string, toDate: string, branchId: number, pageNumber: number, pageSize: number, customerId?: number) => {
      const base = `/api/LedgerReport/GetSalesRevenueReport/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      params.append('customerId', String(customerId !== undefined ? customerId : 0))
      return `${base}?${params.toString()}`
    },
    vendorLedger: (fromDate: string, toDate: string, branchId: number, pageNumber: number, pageSize: number, vendorId?: number) => {
      const base = `/api/LedgerReport/GetVendorLedgerReport/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      // Always include vendorId - send 0 if not provided (for "All Vendors")
      params.append('vendorId', String(vendorId !== undefined ? vendorId : 0))
      return `${base}?${params.toString()}`
    },
    trialBalance: (fromDate: string, toDate: string, branchId: number, pageNumber: number, pageSize: number, withBalancesOnly?: boolean) => {
      const base = `/api/LedgerReport/GetTrialBalance/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      params.append('withBalancesOnly', String(Boolean(withBalancesOnly)))
      return `${base}?${params.toString()}`
    },
    agedReceivables: (agingDate: string, branchId: number, pageNumber: number, pageSize: number, customerId?: number) => {
      const base = `/api/Aging/GetAgeReceivableReport/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('agingDate', agingDate)
      params.append('branchId', String(branchId))
      // Always include customerId - send 0 if not provided (for "All Customers")
      params.append('customerId', String(customerId !== undefined ? customerId : 0))
      return `${base}?${params.toString()}`
    },
    agedPayables: (agingDate: string, branchId: number, pageNumber: number, pageSize: number, vendorId?: number) => {
      const base = `/api/Aging/GetAgePayableReport/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('agingDate', agingDate)
      params.append('branchId', String(branchId))
      // Always include vendorId - send 0 if not provided (for "All Vendors")
      params.append('vendorId', String(vendorId !== undefined ? vendorId : 0))
      return `${base}?${params.toString()}`
    },
    vatReport: (fromDate: string, toDate: string, branchId: number, pageNumber: number, pageSize: number) => {
      const base = `/api/VatReport/GetVatReport/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      return `${base}?${params.toString()}`
    },
    printVatReport: (fromDate: string, toDate: string, branchId: number) => {
      const base = `/api/VatReport/PrintVatReport`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      return `${base}?${params.toString()}`
    },
    fixedAssetReport: (fromDate: string, toDate: string, branchId: number, pageNumber: number, pageSize: number) => {
      const base = `/api/FixedAsset/GetFixedAssetReport/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      return `${base}?${params.toString()}`
    },
    printFixedAssetReport: (fromDate: string, toDate: string, branchId: number) => {
      const base = `/api/FixedAsset/PrintFixedAssetReport`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      return `${base}?${params.toString()}`
    },
    /** GET — Swagger: fromDate, toDate ($date-time), pageNumber, pageSize */
    cogsReport: (fromDate: string, toDate: string, pageNumber: number, pageSize: number) => {
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('pageNumber', String(pageNumber))
      params.append('pageSize', String(pageSize))
      return `/api/CogsReport?${params.toString()}`
    },
    /** GET — Swagger: warehouseId, itemId, reportDate ($date-time), pageNumber, pageSize */
    stockReport: (params: {
      warehouseId: number
      itemId: number
      reportDate: string
      pageNumber: number
      pageSize: number
    }) => {
      const q = new URLSearchParams()
      q.set('warehouseId', String(params.warehouseId))
      q.set('itemId', String(params.itemId))
      q.set('reportDate', params.reportDate)
      q.set('pageNumber', String(params.pageNumber))
      q.set('pageSize', String(params.pageSize))
      return `/api/StockReport?${q.toString()}`
    },
    /** GET — Swagger: fromDate, toDate ($date-time), pageNumber, pageSize; optional warehouseId, itemId */
    salesReport: (params: {
      fromDate: string
      toDate: string
      pageNumber: number
      pageSize: number
      warehouseId?: number
      itemId?: number
    }) => {
      const q = new URLSearchParams()
      q.set('fromDate', params.fromDate)
      q.set('toDate', params.toDate)
      q.set('pageNumber', String(params.pageNumber))
      q.set('pageSize', String(params.pageSize))
      if (params.warehouseId != null && params.warehouseId > 0) {
        q.set('warehouseId', String(params.warehouseId))
      }
      if (params.itemId != null && params.itemId > 0) {
        q.set('itemId', String(params.itemId))
      }
      return `/api/SalesReport?${q.toString()}`
    },
  },

  // File upload endpoints
  files: {
    upload: '/files/upload',                // POST - Upload file
    download: (id: string) => `/files/${id}`, // GET - Download file
    delete: (id: string) => `/files/${id}`,   // DELETE - Delete file
  },

  // Account Management endpoints
  accountManagement: {
    getMaster: '/api/AccountManagement/GetMaster',
    getAllAccounts: (branchId: number) => `/api/AccountManagement/GetAllAccounts/${branchId}`,
    getAccountSettingById: (id: number, branchId: number) => 
      `/api/AccountManagement/GetAccountSettingById/${id}/${branchId}`,
    saveAccountSetting: '/api/AccountManagement/SaveAccountSetting',
  },

  // Voucher/General Entry endpoints
  vouchers: {
    getVoucherTypes: '/api/Voucher/GetVoucherTypes',
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/Voucher/GetAllVoucher/${branchId}/${pageNumber}/${pageSize}`,
    getAllLines: (branchId: number) => `/api/Voucher/GetAllVoucherLines/${branchId}`,
    getById: (id: number, branchId: number) => `/api/Voucher/GetVoucherById/${id}/${branchId}`,
    getLinesByVoucherId: (id: number, branchId: number) =>
      `/api/Voucher/GetVoucherLinesByVoucherId/${id}/${branchId}`,
    getLineById: (id: number, branchId: number) => `/api/Voucher/GetVoucherLineById/${id}/${branchId}`,
    create: '/api/Voucher/CreateVoucher',
    update: '/api/Voucher/UpdateVoucher',
    reverse: (id: number, branchId: number) => `/api/Voucher/ReverseVoucher/${id}/${branchId}`,
  },

  // Customer Advance endpoints
  customerAdvances: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiCustomerAdvance/GetAllAdvance/${branchId}/${pageNumber}/${pageSize}`,
    getById: (branchId: number, id: number) =>
      `/api/ApiCustomerAdvance/GetAllAdvanceById/${branchId}/${id}`,
    getAllAccounts: (branchId: number) =>
      `/api/ApiCustomerAdvance/GetAllAccounts/${branchId}`,
    insert: '/api/ApiCustomerAdvance/Insert',
    update: '/api/ApiCustomerAdvance/Update',
    postData: '/api/ApiCustomerAdvance/PostData',
  },

  // Vendor Advance endpoints
  vendorAdvances: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiVendorAdvance/GetAllAdvance/${branchId}/${pageNumber}/${pageSize}`,
    getById: (branchId: number, id: number) =>
      `/api/ApiVendorAdvance/GetAllAdvanceById/${branchId}?id=${id}`,
    getAllAccounts: (branchId: number) =>
      `/api/ApiVendorAdvance/GetAllAccounts/${branchId}`,
    insert: '/api/ApiVendorAdvance/Insert',
    update: '/api/ApiVendorAdvance/Update',
    postData: '/api/ApiVendorAdvance/PostData',
  },

  // Sales Receipts endpoints
  receipts: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiReceipts/GetAllRecepit/${branchId}/${pageNumber}/${pageSize}`,
    getById: (branchId: number, id: number) =>
      `/api/ApiReceipts/GetAllRecepitById/${branchId}?id=${id}`,
    getCustomerInvoices: (customerId: number) =>
      `/api/ApiReceipts/GetCustomerInvoices/${customerId}`,
    getCustomerAdvances: (customerId: number, receiptId: number) =>
      `/api/ApiReceipts/getCustomerAdvances/${customerId}/${receiptId}`,
    getCustomerAdvanceLinesByReceiptId: (receiptId: number) =>
      `/api/ApiReceipts/GetCustomerAdvanceLinesByReceiptId/${receiptId}`,
    getReceiptLinesByReceiptId: (receiptId: number) =>
      `/api/ApiReceipts/GetReceiptLinesByReceiptId/${receiptId}`,
    getAllAccounts: (branchId: number) =>
      `/api/ApiReceipts/GetAllAccounts/${branchId}`,
    insert: '/api/ApiReceipts/Insert',
    update: '/api/ApiReceipts/Update',
    delete: (id: number, branchId: number) => `/api/ApiReceipts/Delete/${id}/${branchId}`,
  },

  // Receipt Return endpoints
  receiptReturn: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ReceiptReturn/GetReceiptReturns/${branchId}/${pageNumber}/${pageSize}`,
    getInvoicesByCustomerId: (customerId: number) =>
      `/api/ReceiptReturn/GetInvoicesByCustomerId?customerId=${customerId}`,
    getInsertData: (branchId: number, invoiceId: number) =>
      `/api/ReceiptReturn/GetInsertData/${branchId}?invoiceId=${invoiceId}`,
    getById: (id: number) => `/api/ReceiptReturn/GetReceiptReturnById?id=${id}`,
    save: '/api/ReceiptReturn',
    update: '/api/ReceiptReturn',
  },

  // Payment Return endpoints
  paymentReturn: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/PaymentReturn/GetPaymentReturns/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/PaymentReturn/GetPaymentReturnById/${id}/${branchId}`,
    getBillsByVendorId: (vendorId: number, includeBillId?: number) =>
      `/api/PaymentReturn/GetBillsByVendorId?vendorId=${vendorId}${includeBillId ? `&includeBillId=${includeBillId}` : ''}`,
    getInsertData: (branchId: number, billId: number) =>
      `/api/PaymentReturn/GetInsertData/${branchId}?billId=${billId}`,
    save: '/api/PaymentReturn',
    update: '/api/PaymentReturn',
    delete: (id: number) => `/api/PaymentReturn/${id}`,
  },

  // Vendor management endpoints
  vendors: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/Vendor/GetAllVendors/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/Vendor/GetVendorById/${id}/${branchId}`,
    save: '/api/Vendor/SaveVendor',
    update: '/api/Vendor/UpdateVendor',
    delete: (id: number, ipAddress: string) =>
      `/api/Vendor/DeleteVendor/${id}/${ipAddress}`,
    getStates: '/api/ApiBranch/GetStateLookups',
    getCountries: '/api/ApiBranch/GetCountryLookups',
    getCities: (countryId?: number) => {
      const base = '/api/ApiBranch/GetCityLookups'
      if (countryId !== undefined) {
        return `${base}?countryId=${countryId}`
      }
      return base
    },
  },

  // Purchase Bills endpoints
  bills: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiBills/GetAllBillList/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number) =>
      `/api/ApiBills/GetBillById/${id}`,
    getBillLines: (branchId: number, id: number) =>
      `/api/ApiBills/GetBillLinesByBillId/${branchId}/${id}`,
    getAllVendors: (branchId: number) => `/api/ApiBills/GetAllVendors/${branchId}`,
    getAllBillLines: (branchId: number) => `/api/ApiBills/GetAllBillLines/${branchId}`,
    getProject: (branchId: number) => `/api/ApiBills/GetProject/${branchId}`,
    getItemList: (branchId: number) => `/api/ApiBills/GetItemList/${branchId}`,
    getTaxRates: (branchId: number) => `/api/ApiBills/GetTaxRates/${branchId}`,
    getCostLocation: '/api/ApiBills/GetCostLocation',
    getProfitCenter: '/api/ApiBills/GetProfitCenter',
    getDepartments: '/api/ApiBills/GetDepartments',
    getPaymentTerms: '/api/ApiBills/GetlookuppaymentTerms',
    getAllAccounts: (branchId: number) => `/api/ApiBills/GetAllAccounts/${branchId}`,
    insert: '/api/ApiBills/Insert',
    update: '/api/ApiBills/Update',
    postData: (id: number, ipAddress?: string) => {
      let url = `/api/ApiBills/PostData?id=${id}`
      if (ipAddress) {
        url += `&IpAddress=${encodeURIComponent(ipAddress)}`
      }
      return url
    },
  },

  // Vendor Refunds (Credit Notes) endpoints
  refunds: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiRefunds/GetAllRefunds/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number) => `/api/ApiRefunds/GetRefundById/${id}`,
    getRefundLines: (branchId: number, id: number) =>
      `/api/ApiRefunds/GetRefundLinesByRefundId/${branchId}/${id}`,
    getAllVendors: (branchId: number) => `/api/ApiRefunds/GetAllVendors/${branchId}`,
    getAllRefundLines: (branchId: number) => `/api/ApiRefunds/GetAllRefundLines/${branchId}`,
    getAllBills: (branchId: number) => `/api/ApiRefunds/GetAllBills/${branchId}`,
    getBillLines: (branchId: number, billId: number) =>
      `/api/ApiRefunds/GetBillsLines/${billId}/${branchId}`,
    insert: '/api/ApiRefunds/Insert',
    update: '/api/ApiRefunds/Update',
    postData: (id: number, ipAddress?: string) => {
      let url = `/api/ApiRefunds/PostData?id=${id}`
      if (ipAddress) {
        url += `&IpAddress=${encodeURIComponent(ipAddress)}`
      }
      return url
    },
  },

  // Purchase Payments endpoints
  payments: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/Payments/GetAllPayments/${branchId}/${pageNumber}/${pageSize}`,
    getById: (branchId: number, id: number) =>
      `/api/Payments/GetAllPaymentsById/${branchId}?id=${id}`,
    getAllBills: (branchId: number) =>
      `/api/Payments/GetAllBills/${branchId}`,
    getAllAdvance: (branchId: number) =>
      `/api/Payments/GetAllAdvance/${branchId}`,
    getVendorsBills: (vendorId: number) =>
      `/api/Payments/GetVendorsBills/${vendorId}`,
    getVendorsAdvance: (vendorId: number, advanceId: number) =>
      `/api/Payments/GetVendorsAdvance/${vendorId}/${advanceId}`,
    getVendorAdvanceLineByAdvanceId: (advanceId: number) =>
      `/api/Payments/GetVendorAdvanceLineByAdvanceId/${advanceId}`,
    getVendorAdvanceLinesByPaymentId: (paymentId: number) =>
      `/api/Payments/GetVendorAdvanceLinesByPaymentId/${paymentId}`,
    getPaymentLinesByPaymentId: (paymentId: number) =>
      `/api/Payments/GetPaymentLinesByPaymentId/${paymentId}`,
    getAllAccounts: (branchId: number) =>
      `/api/Payments/GetAllAccounts/${branchId}`,
    insert: '/api/Payments/Insert',
    update: '/api/Payments/Update',
    delete: (id: number, branchId: number) => `/api/Payments/Delete/${id}/${branchId}`,
  },

  // Fixed Asset endpoints
  fixedAssets: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/FixedAsset/GetAllAssets/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/FixedAsset/GetAssetById/${id}/${branchId}`,
    getByType: (id: number, branchId: number) =>
      `/api/FixedAsset/GetAssetsByType/${id}/${branchId}`,
    save: '/api/FixedAsset/Save',
    update: '/api/FixedAsset/Update',
    delete: (id: number) => `/api/FixedAsset/Delete/${id}`,
    getDepMethods: '/api/FixedAsset/GetDepMethods',
    getDepreciationMethodLookups: '/api/FixedAsset/GetDepreciationMethodLookups',
    getAssetCategoryLookups: '/api/FixedAsset/GetAssetCategoryLookups',
    getAssetTypes: '/api/FixedAsset/GetAssetTypes',
    depreciationRun: (branchId: number, monthYear: string) =>
      `/api/FixedAsset/DepreciationRun?branchId=${branchId}&MonthYear=${monthYear}`,
    depreciationRunStatus: (branchId: number) =>
      `/api/FixedAsset/GetDepreciationRunStatus/${branchId}`,
    // Asset Disposal endpoints
    disposal: {
      getAll: (branchId: number, pageNumber: number, pageSize: number) =>
        `/api/FixedAsset/GetAssetDisposals/${branchId}/${pageNumber}/${pageSize}`,
      save: '/api/FixedAsset/AssetDisposal',
    },
  },

  // GOSI Setting endpoints
  gosiSetting: {
    get: (branchId: number) => `/api/GosiSetting/GetGosi?branchId=${branchId}`,
    addOrUpdate: '/api/GosiSetting/AddOrUpdateGosi',
  },

  // Late Policy Setting endpoints
  latePolicySetting: {
    saveOrUpdate: '/api/LatePolicySetting/SaveOrUpdate',
    getDeductionMethods: '/api/LatePolicySetting/GetDeductionMethods',
    getByBranchId: (branchId: number) => `/api/LatePolicySetting/GetByBranchId?branchId=${branchId}`,
  },

  // Leave Policy Setting endpoints
  leavePolicySetting: {
    saveOrUpdate: '/api/LeavePolicySetting/SaveOrUpdate',
    getByBranchId: (branchId: number) => `/api/LeavePolicySetting/GetByBranchId?branchId=${branchId}`,
  },

  // Self Service - Hire Employee Request
  // (moved to `modules/self-services/api/endpoints.ts`)
} as const

// Helper function to build URL with query parameters
export const buildUrl = (endpoint: string, params?: Record<string, any>): string => {
  if (!params) return endpoint
  
  const url = new URL(endpoint, 'http://localhost')
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.append(key, String(value))
    }
  })
  
  return url.pathname + url.search
}

// Common query parameters for list endpoints
export interface ListParams {
  page?: number
  pageSize?: number
  search?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  filters?: Record<string, any>
}

// Helper to build list URLs with common parameters
export const buildListUrl = (endpoint: string, params?: ListParams): string => {
  return buildUrl(endpoint, params)
}
